import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { SkeletonLine } from "../components/Skeleton";
import { useToast } from "../components/Toast";
import type { AdminDashboardStats } from "../types";

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 60 * 60 * 24 * 365],
  ["month", 60 * 60 * 24 * 30],
  ["week", 60 * 60 * 24 * 7],
  ["day", 60 * 60 * 24],
  ["hour", 60 * 60],
  ["minute", 60],
  ["second", 1],
];
const rtf = typeof Intl !== "undefined" ? new Intl.RelativeTimeFormat("en", { numeric: "auto" }) : null;

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const delta = (then - Date.now()) / 1000;
  const abs = Math.abs(delta);
  for (const [unit, secs] of RELATIVE_UNITS) {
    if (abs >= secs || unit === "second") {
      const val = Math.round(delta / secs);
      return rtf ? rtf.format(val, unit) : `${Math.abs(val)} ${unit}s ${val < 0 ? "ago" : "from now"}`;
    }
  }
  return iso;
}

function kindLabel(kind: string): string {
  return kind.replace(/_/g, " ");
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<AdminDashboardStats>("/api/admin/dashboard");
        if (!cancelled) setStats(data);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load dashboard";
        setErr(msg);
        toast.error("Dashboard failed to load", msg);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  return (
    <div>
      <div className="admin-content-header">
        <div>
          <div className="admin-content-title">Dashboard</div>
          <div className="admin-content-sub">
            <span className="live-dot" aria-hidden="true" /> Platform snapshot, updated live.
          </div>
        </div>
      </div>

      {err && <div className="alert alert-error" role="alert">{err}</div>}

      <div className="card-grid">
        <CardTile to="/admin/clients" title="Active clients" value={stats?.activeClients} />
        <CardTile title="Client users" value={stats?.clientUsers} />
        <CardTile title="Pending invites" value={stats?.pendingInvites} />
        <CardTile title="Prompt logs today" value={stats?.promptLogsToday} />
      </div>

      <section className="section-block">
        <h2 className="section-heading">Recent activity</h2>
        {!stats ? (
          <div className="stack-2" style={{ marginTop: "var(--space-3)" }}>
            <SkeletonLine width="64%" />
            <SkeletonLine width="52%" />
            <SkeletonLine width="70%" />
          </div>
        ) : stats.recentActivity.length === 0 ? (
          <EmptyState
            heading="No activity yet"
            body="Create a client and invite a user to get started."
            action={<Link to="/admin/clients" className="btn btn-primary">Create a client</Link>}
          />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }}>When</th>
                <th style={{ width: "20%" }}>Kind</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentActivity.map((a, i) => (
                <tr key={i}>
                  <td className="text-muted num" style={{ whiteSpace: "nowrap" }}>
                    <time dateTime={a.at} title={new Date(a.at).toLocaleString()}>
                      {relativeTime(a.at)}
                    </time>
                  </td>
                  <td>
                    <span className="badge">{kindLabel(a.kind)}</span>
                  </td>
                  <td>{a.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function CardTile({ to, title, value }: { to?: string; title: string; value?: number }) {
  const body = (
    <>
      <div className="card-title">{title}</div>
      <div className="card-value">
        {value === undefined ? <SkeletonLine width="60%" height={40} /> : value}
      </div>
    </>
  );
  return to ? (
    <Link to={to} className="card">{body}</Link>
  ) : (
    <div className="card">{body}</div>
  );
}
